import type { SxProps, Theme } from '@mui/material/styles';

/**
 * The three animations the app has. **Why each is shaped the way it is —
 * including why 400 / 180 / 200 are three numbers rather than one — is
 * `docs/adr/0005-the-page-transition.md`.** Do not unify them.
 */

const DURATION_MS = 400;

/**
 * The `sx` for the page container, remounted on every route change by a `key` on
 * the outlet — so the animation replays without any JavaScript deciding when.
 *
 * **`prefers-reduced-motion` removes it entirely** rather than shortening it: a
 * fast scale is still a scale.
 */
export const pageEnterSx: SxProps<Theme> = {
  '@keyframes augmentPageEnter': {
    from: { opacity: 0, transform: 'scale(0.98)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: `augmentPageEnter ${String(DURATION_MS)}ms ease-out both`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
};

/**
 * The settle after a filter changes: opacity only, and **starting at 0.6 rather
 * than 0, which is the whole trick.** A keyed remount can only animate the
 * incoming content, so fading up from zero reads as a flash to blank and back —
 * more jarring than the jump it replaces.
 */
const SETTLE_MS = 180;

export const filterSettleSx: SxProps<Theme> = {
  '@keyframes augmentFilterSettle': {
    from: { opacity: 0.6 },
    to: { opacity: 1 },
  },
  animation: `augmentFilterSettle ${String(SETTLE_MS)}ms ease-out both`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
  },
};

/**
 * How long a disclosure takes to open — the search filters below desktop, a
 * process card's hidden methods.
 *
 * **Pass `0` under `prefers-reduced-motion`.** MUI's `Collapse` animates a
 * height in JavaScript, so unlike the CSS above it cannot opt itself out.
 */
export const DISCLOSURE_MS = 200;

/** The query to hand `useMediaQuery` when deciding that. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
